import React, { FC } from 'react'
import classNamesBind from 'classnames/bind'
import css from './styles.module.scss'
import { useTeamShowSimple } from '@dashboard/hooks/team/useTeamShowSimple'
import LoadingBox from '@dashboard/components/LoadingBox'
import TeamInviteForm from '../TeamInviteForm'
import { TeamSimple } from '@dashboard/lib/types'
import { AUTH_QUERY_PARAMS_STORAGE_KEY } from '@dashboard/components/Registration/component'
import * as ls from 'local-storage'
import { rsEvent } from '@dashboard/lib/analytics'
import { useAppDispatch } from '@dashboard/hooks/useAppDispatch'
import { runTasks } from '@dashboard/store/actions/tasks'
import { ParsedUrlQuery } from 'querystring'
import { getQueryParam } from '@dashboard/lib/browser'
import { handleTasks } from '@dashboard/lib/user'
import { useAppStateSelector } from '@dashboard/hooks/useAppStateSelector'
import { useOnSuccess } from '@dashboard/hooks/useOnSuccess'
import { createToast } from '@dashboard/store/actions/user/toast/create'
import { v4 as uuidv4 } from 'uuid'

interface OwnProps {
  teamId: string
  authQueryParams: ParsedUrlQuery
  toggleModal: () => void
}

const classNames = classNamesBind.bind(css)

const LinkInviteFlow: FC<OwnProps> = ({
  teamId,
  authQueryParams,
  toggleModal,
}: OwnProps) => {
  const inviterId = getQueryParam(authQueryParams.inviter_id)
  const { inviteTasksState } = useAppStateSelector(state => state.profile)
  const { state, result: team } = useTeamShowSimple(teamId, inviterId)
  const dispatch = useAppDispatch()

  // Functions
  const inviterInfo = ({
    inviter_info,
  }: TeamSimple):
    | {
        name: string
        photoURL: string | null
      }
    | undefined => {
    if (!inviter_info) return undefined

    return {
      name: inviter_info.name,
      photoURL: inviter_info.photo_url,
    }
  }

  const removeAuthQueryParams = () => {
    ls.remove(AUTH_QUERY_PARAMS_STORAGE_KEY)
  }

  const accept = () => {
    rsEvent('Invite Accepted', {
      organization_id: teamId,
      inviter_name: team?.inviter_info?.name || 'NA',
    })

    handleTasks(
      (...args: Parameters<typeof runTasks>) => dispatch(runTasks(...args)),
      authQueryParams,
    )
  }

  const decline = () => {
    rsEvent('Invite Dismissed', {
      organization_id: teamId,
      inviter_name: team?.inviter_info?.name || 'NA',
    })

    removeAuthQueryParams()
    toggleModal()
  }

  // Effects
  useOnSuccess([inviteTasksState], () => {
    toggleModal()
    removeAuthQueryParams()
    dispatch(
      createToast({
        id: uuidv4(),
        title: `You've successfully joined ${team ? team.name : 'this team'}.`,
        subtitle:
          "Revisit onboarding tutorials by clicking 'learn' in the :hamburgerMenu: menu at the top of your dashboard",
        icon: {
          name: 'checkBubble',
          discType: 'S',
        },
      }),
    )
  })

  if (state === 'failure')
    return (
      <div className={classNames('failure')}>Failed to load invite data.</div>
    )

  if (!team) return <LoadingBox text="Loading invite data" />

  return (
    <TeamInviteForm
      teamName={team.name}
      inviterInfo={inviterInfo(team)}
      licenseInfo={team.licenses}
      loading={inviteTasksState === 'loading'}
      accept={accept}
      decline={decline}
    />
  )
}

export default LinkInviteFlow
